import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { isExternal } from "@/components/CustomContent";

const PLACES = [["nav", "Top menu, under \"More\" (also in the mobile menu)"], ["hero", "Home page hero buttons"], ["footer", "Footer links"]];
const POSITIONS = [["top", "Home: before the books"], ["after_books", "Home: after the books"], ["after_sections", "Home: after the sections"], ["after_map", "Home: after the map"], ["after_recent", "Home: after recent pages (bottom)"]];
const newId = () => Math.random().toString(36).slice(2, 10);
const URL_MODE = "__url__";

const useLinkOptions = () => {
  const { sections, series } = useSite();
  const [pages, setPages] = useState([]);
  useEffect(() => { api.get("/articles/titles").then((r) => setPages(r.data.filter((p) => p.published))); }, []);
  return [
    ["Main pages", [["/", "Home"], ["/codex", "All Pages A–Z"], ["/map", "Map of the Realms"], ["/vault", "Royal Vault"], ["/join", "Reader Contribution"]]],
    ["Series", series.map((s) => [`/series/${s.slug}`, s.title])],
    ["Sections", sections.map((s) => [`/section/${s.slug}`, s.name])],
    ["Articles", pages.map((p) => [`/wiki/${p.slug}`, p.title])],
  ];
};

const LinkField = ({ value, onChange, options, testid }) => {
  const known = options.some(([, list]) => list.some(([h]) => h === value));
  const [url, setUrl] = useState(isExternal(value) || (value && !known));
  return (
    <div className="grid gap-2">
      <select data-testid={`${testid}-select`} className="admin-input" value={url ? URL_MODE : value}
        onChange={(e) => { if (e.target.value === URL_MODE) { setUrl(true); onChange(""); } else { setUrl(false); onChange(e.target.value); } }}>
        <option value="">Choose where it goes…</option>
        {options.map(([g, list]) => list.length > 0 && <optgroup key={g} label={g}>{list.map(([h, l]) => <option key={h} value={h}>{l}</option>)}</optgroup>)}
        <option value={URL_MODE}>Web address (shop, newsletter…)</option>
      </select>
      {url && <input data-testid={`${testid}-url`} className="admin-input" placeholder="https://…" value={value} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
};

const Field = ({ label, children, className = "" }) => <label className={`block ${className}`}><span className="admin-label">{label}</span>{children}</label>;

const Controls = ({ i, n, move, remove, testid }) => (
  <div className="flex gap-1 justify-end">
    <button type="button" data-testid={`${testid}-up`} disabled={i === 0} onClick={() => move(i, -1)} className="p-2 border border-[#c9a227]/50 disabled:opacity-30" title="Move up"><ArrowUp className="w-4 h-4" /></button>
    <button type="button" data-testid={`${testid}-down`} disabled={i === n - 1} onClick={() => move(i, 1)} className="p-2 border border-[#c9a227]/50 disabled:opacity-30" title="Move down"><ArrowDown className="w-4 h-4" /></button>
    <button type="button" data-testid={`${testid}-delete`} onClick={() => remove(i)} className="p-2 border border-[#991b1b]/50 text-[#991b1b] hover:bg-[#991b1b]/10" title="Delete"><Trash2 className="w-4 h-4" /></button>
  </div>
);

const useList = (initial) => {
  const [list, setList] = useState(initial);
  const update = (i, patch) => setList((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setList((l) => { const c = [...l]; [c[i], c[i + d]] = [c[i + d], c[i]]; return c; });
  const remove = (i) => setList((l) => l.filter((_, j) => j !== i));
  return { list, setList, update, move, remove };
};

const ButtonsEditor = ({ b, options }) => (
  <section data-testid="custom-buttons-section" className="vellum p-6 sm:p-8 mb-10">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div><p className="eyebrow">Custom buttons</p><p className="text-sm italic text-[#4a3a2a]">Extra link buttons in the top menu, the home page hero or the footer.</p></div>
      <button type="button" data-testid="custom-add-button" onClick={() => b.setList((l) => [...l, { id: newId(), text: "", href: "", place: "nav" }])} className="btn-ghost-illum"><Plus className="w-4 h-4" /> Add button</button>
    </div>
    {b.list.length === 0 && <p data-testid="custom-buttons-empty" className="italic text-[#5e4a35] py-4">No custom buttons yet.</p>}
    {b.list.map((x, i) => (
      <div key={x.id} data-testid={`custom-button-row-${i}`} className="grid md:grid-cols-12 gap-3 items-end py-4 border-b border-[#c9a227]/25">
        <Field label="Button text" className="md:col-span-3"><input data-testid={`custom-button-text-${i}`} maxLength={40} className="admin-input" value={x.text} onChange={(e) => b.update(i, { text: e.target.value })} /></Field>
        <Field label="Where it appears" className="md:col-span-3"><select data-testid={`custom-button-place-${i}`} className="admin-input" value={x.place} onChange={(e) => b.update(i, { place: e.target.value })}>{PLACES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="Goes to" className="md:col-span-4"><LinkField testid={`custom-button-link-${i}`} value={x.href} options={options} onChange={(v) => b.update(i, { href: v })} /></Field>
        <div className="md:col-span-2"><Controls i={i} n={b.list.length} move={b.move} remove={b.remove} testid={`custom-button-${i}`} /></div>
      </div>
    ))}
  </section>
);

const BlocksEditor = ({ k, options }) => (
  <section data-testid="custom-blocks-section" className="vellum p-6 sm:p-8">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div><p className="eyebrow">Custom headings</p><p className="text-sm italic text-[#4a3a2a]">New home page sections with a heading, optional text and an optional button.</p></div>
      <button type="button" data-testid="custom-add-block" onClick={() => k.setList((l) => [...l, { id: newId(), eyebrow: "", title: "", text: "", button_text: "", button_href: "", position: "after_recent" }])} className="btn-ghost-illum"><Plus className="w-4 h-4" /> Add heading</button>
    </div>
    {k.list.length === 0 && <p data-testid="custom-blocks-empty" className="italic text-[#5e4a35] py-4">No custom headings yet.</p>}
    {k.list.map((x, i) => (
      <div key={x.id} data-testid={`custom-block-row-${i}`} className="grid md:grid-cols-12 gap-3 py-5 border-b border-[#c9a227]/25">
        <Field label="Small heading (optional)" className="md:col-span-4"><input data-testid={`custom-block-eyebrow-${i}`} maxLength={80} className="admin-input" value={x.eyebrow} onChange={(e) => k.update(i, { eyebrow: e.target.value })} /></Field>
        <Field label="Main heading" className="md:col-span-5"><input data-testid={`custom-block-title-${i}`} maxLength={120} className="admin-input" value={x.title} onChange={(e) => k.update(i, { title: e.target.value })} /></Field>
        <Field label="Position" className="md:col-span-3"><select data-testid={`custom-block-position-${i}`} className="admin-input" value={x.position} onChange={(e) => k.update(i, { position: e.target.value })}>{POSITIONS.map(([p, l]) => <option key={p} value={p}>{l}</option>)}</select></Field>
        <Field label="Text (optional)" className="md:col-span-12"><textarea data-testid={`custom-block-text-${i}`} rows={3} maxLength={2000} className="admin-input" value={x.text} onChange={(e) => k.update(i, { text: e.target.value })} /></Field>
        <Field label="Button text (optional)" className="md:col-span-4"><input data-testid={`custom-block-btn-text-${i}`} maxLength={40} className="admin-input" value={x.button_text} onChange={(e) => k.update(i, { button_text: e.target.value })} /></Field>
        <Field label="Button goes to" className="md:col-span-5"><LinkField testid={`custom-block-btn-link-${i}`} value={x.button_href} options={options} onChange={(v) => k.update(i, { button_href: v })} /></Field>
        <div className="md:col-span-3 self-end"><Controls i={i} n={k.list.length} move={k.move} remove={k.remove} testid={`custom-block-${i}`} /></div>
      </div>
    ))}
  </section>
);

export default function AdminCustom() {
  const { settings, refresh } = useSite();
  const options = useLinkOptions();
  const b = useList([]);
  const k = useList([]);
  const [busy, setBusy] = useState(false);
  const { setList: setB } = b;
  const { setList: setK } = k;
  useEffect(() => { setB(settings.custom_buttons || []); }, [settings.custom_buttons, setB]);
  useEffect(() => { setK(settings.custom_blocks || []); }, [settings.custom_blocks, setK]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/custom", { buttons: b.list, blocks: k.list }); await refresh(); toast.success("Saved — now live on the site"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const SaveBtn = ({ testid }) => <button data-testid={testid} onClick={save} disabled={busy} className="btn-illum theme-flame disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}</button>;
  return (
    <div data-testid="admin-custom">
      <AdminTitle eyebrow="Your own additions" title="Custom Buttons & Headings"><SaveBtn testid="custom-save" /></AdminTitle>
      <p className="mb-8 italic text-[#4a3a2a] max-w-3xl">Add your own buttons and home page headings, change their order, or delete them. To hide one of the built-in headings or buttons, use Shown / Hidden on the Site Wording page.</p>
      <ButtonsEditor b={b} options={options} />
      <BlocksEditor k={k} options={options} />
      <div className="mt-8"><SaveBtn testid="custom-save-bottom" /></div>
    </div>
  );
}
